import React, { MouseEventHandler } from 'react';
import { CIActionBtnGroup, CIActionGuard } from 'ccs-ui-components';
import { IExceptionSearchModel } from '../../models';
import { requiredAuthPermission } from '../../../common/constants/UserPermissionConstants';
import { IExceptionFilterState } from '../../store/reducer/exceptions/ExceptionFilterReducer';

/**
 * @method renderExceptionIconButtons
 * @description Renders the exception icon buttons.
 * @param row - The exception search model.
 * @returns The rendered exception icon buttons.
 */

export interface IExceptionIconprops {
  row: IExceptionSearchModel,
  viewBtnClickHandler: (data: IExceptionSearchModel) => void,
  editBtnClickHandler: (data: IExceptionSearchModel) => void,
  permission: string[] | null
}
export const renderExceptionIconButtons = ({ row, viewBtnClickHandler, editBtnClickHandler, permission }: IExceptionIconprops) => {
  const actionButtons: {
    label: string;
    key: 'editIcon' | 'deleteIcon' | 'viewIcon' | 'addFillIcon' | 'addIcon' | 'refreshIcon' | 'bellIcon' | 'rightArrowIcon' | 'leftArrowIcon' | 'xMarkLargeIcon' | 'xMarkSmallIcon' | 'bottonArrowIcon' | 'upArrowIcon' | 'caretDownIcon' | 'caretUpIcon' | 'caretLeftIcon' | 'caretRightIcon' | 'caretDownFillIcon' | 'caretUpFillIcon' | 'caretLeftFillIcon' | 'caretRightFillIcon' | 'infoIcon' | 'infoFillIcon' | 'chatTextIcon' | 'chatTextFillIcon' | 'exclamationIcon' | 'exclamationFillIcon' | 'checkCircleIcon' | 'checkCircleFillIcon' | 'sortAscIcon' | 'sortDescIcon' | 'sortDisableIcon' | 'flowDiagramIcon' | 'hamBurgerIcon' | 'directionRightIcon' | 'uploadIcon' | 'xCircleIcon' | 'copyIcon' | 'largeCheckIcon' | 'checkIcon' | 'thinCheckIcon';
    icon?: React.ReactElement;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
    className?: string;
    variant?: string;
    testId?: string;
  }[] = [
    {
      label: 'view',
      key: 'viewIcon',
      onClick: () => viewBtnClickHandler(row),
      testId: 'viewException'
    }
  ];

  if (CIActionGuard([requiredAuthPermission.CP.edit], permission)) {
    actionButtons.push({
      label: 'edit',
      key: 'editIcon',
      onClick: () => editBtnClickHandler(row),
      testId: 'editException'
    });
  }

  return (
    <div data-testid="actionBtn">
      <CIActionBtnGroup
        buttons={actionButtons}
      />
    </div>
  );
};

export const parseSortString = (val?: string) => {
  if (!val) return { sort: '', type: '' };
  const [sort, type] = val.split(',');
  return { sort: sort ?? '', type: type ?? '' };
};

/**
 * Gets current filter parameters for Exceptions.
 * If the search url has params use those; otherwise fall back to Redux filter state.
 */
export const getFilterParams = (search: string, filterState: IExceptionFilterState) => {
  const params = new URLSearchParams(search);
  const parsedParams = {
    exceptionTypes: params.get('exceptionTypes') ?? '',
    exceptionNames: params.get('exceptionNames') ?? '',
    fromDate: params.get('fromDate') ?? '',
    toDate: params.get('toDate') ?? '',
    page: params.get('page') ?? '',
    item: params.get('item') ?? '',
    sort: params.get('sort') ?? '',
    type: params.get('type') ?? ''
  };

  const hasUrlFilter = parsedParams.exceptionTypes
    || parsedParams.exceptionNames
    || parsedParams.fromDate
    || parsedParams.toDate
    || parsedParams.sort
    || parsedParams.type;

  if (hasUrlFilter) {
    return parsedParams;
  }

  const { sort, type } = parseSortString(filterState.sort);
  return {
    exceptionTypes: filterState.status ?? '',
    exceptionNames: filterState.searchText ?? '',
    fromDate: String(filterState.fromDate ?? ''),
    toDate: String(filterState.toDate ?? ''),
    page: params.get('page') ?? '',
    item: params.get('item') ?? '',
    sort,
    type
  };
};
